import decode from 'jwt-decode';
import config from 'config';
import { makeRequest, post, ApiError } from './common';
import { getAuth, refreshAccessToken } from 'src/helpers/auth';

const CLIENT_ID = 'jg.web';
const REGISTRATION_SOURCE = 6;

export const ERROR_INVALID_CREDENTIALS = 'ERROR_INVALID_CREDENTIALS';
export const ERROR_EMAIL_ADDRESS_IN_USE = 'ERROR_EMAIL_ADDRESS_IN_USE';
export const ERROR_NO_CONSUMER_PERMISSIONS = 'ERROR_NO_CONSUMER_PERMISSIONS';

export interface LoginOptions {
  emailAddress: string;
  password: string;
}

export interface LoginResult {
  success: boolean;
  error?:
    | 'ERROR_INVALID_CREDENTIALS'
    | 'ERROR_EMAIL_ADDRESS_IN_USE'
    | 'ERROR_NO_CONSUMER_PERMISSIONS';
}

interface UserConsents {
  consentGiven: boolean;
  id: number;
  entityId: number;
  text: string;
}

export async function login({
  emailAddress,
  password,
}: LoginOptions): Promise<LoginResult> {
  const payload = {
    Username: emailAddress,
    Password: password,
    GrantType: 'password',
    ClientId: CLIENT_ID,
  };

  const options = {
    baseUri: config.IAM_API_URI,
    throwOnError: false,
    autoRefreshAccessToken: false,
  };

  const response = await post('/account/login/justgiving', payload, options);

  if (isInvalidCredentialsResponse(response)) {
    return { success: false, error: ERROR_INVALID_CREDENTIALS };
  }

  if (!response.ok) {
    throw new ApiError({ response });
  }

  if (!hasConsumerPermissions(response.body.Result.access_token)) {
    return { success: false, error: ERROR_NO_CONSUMER_PERMISSIONS };
  }

  return { success: true };
}

interface LoginWithFacebookOptions {
  firstName: string;
  lastName: string;
  accessToken: string;
  emailAddress: string;
}

export async function loginWithFacebook({
  firstName,
  lastName,
  accessToken,
  emailAddress,
}: LoginWithFacebookOptions): Promise<LoginResult> {
  const payload = {
    FirstName: firstName,
    LastName: lastName,
    AccessToken: accessToken,
    EmailAddress: emailAddress,
    RegistrationSource: REGISTRATION_SOURCE,
    LongLived: true,
    GrantType: 'facebook',
    ClientId: CLIENT_ID,
  };

  const options = {
    baseUri: config.IAM_API_URI,
    throwOnError: false,
    autoRefreshAccessToken: false,
  };

  const response = await post('/account/login/facebook', payload, options);

  if (!response.ok) {
    throw new ApiError({ response });
  }

  return { success: true };
}

export interface RegisterAndLoginOptions {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  userConsents: {
    collectionLocationId?: number;
    consents: UserConsents[];
  };
}

export async function registerAndLogin({
  firstName,
  lastName,
  emailAddress,
  password,
  userConsents,
}: RegisterAndLoginOptions): Promise<LoginResult> {
  const payload = {
    FirstName: firstName,
    LastName: lastName,
    EmailAddress: emailAddress,
    Password: password,
    GrantType: 'register_justgiving',
    ClientId: CLIENT_ID,
    RegistrationSource: REGISTRATION_SOURCE,
    UserConsents: {
      CollectionLocationId: userConsents.collectionLocationId,
      Consents: userConsents.consents.map(consent => ({
        ConsentGiven: consent.consentGiven,
        ConsentStatementText: consent.text,
        ConsentStatementId: consent.id,
        EntityId: consent.entityId,
      })),
    },
  };

  const options = {
    baseUri: config.IAM_API_URI,
    throwOnError: false,
    autoRefreshAccessToken: false,
  };

  const response = await post('/account/register-and-login', payload, options);

  if (isEmailAddressInUseResponse(response)) {
    return { success: false, error: ERROR_EMAIL_ADDRESS_IN_USE };
  }

  if (!response.ok) {
    throw new ApiError({ response });
  }

  return { success: true };
}

export interface ChangeNameOptions {
  firstName: string;
  lastName: string;
}

export async function changeName(
  userId: string,
  { firstName, lastName }: ChangeNameOptions
) {
  await refreshAccessToken();

  const auth = getAuth();
  const refreshToken = auth.getRefreshToken();

  await makeRequest({
    baseUri: config.IAM_API_URI,
    path: `/users/${userId}/change-name`,
    method: 'post',
    throwOnError: true,
    data: {
      userGuid: userId,
      Title: 'Other',
      FirstName: firstName,
      LastName: lastName,
      RefreshToken: refreshToken,
    },
    withBearerAuthHeader: true,
  });
}

interface ChangeProfilePictureOptions {
  userId: string;
  imagePayload: File;
}

export async function changeProfilePicture({
  userId,
  imagePayload,
}: ChangeProfilePictureOptions): Promise<string> {
  await refreshAccessToken();

  const auth = getAuth();
  const refreshToken = auth.getRefreshToken();

  const body = new FormData();
  body.append('ProfileImage', imagePayload);
  body.append('RefreshToken', refreshToken!);

  const res = await makeRequest({
    baseUri: config.IAM_API_URI,
    path: `/users/${userId}/profile-image`,
    method: 'put',
    throwOnError: true,
    data: body,
    withBearerAuthHeader: true,
  });

  return res.body.Result;
}

interface UploadProfilePicFacebookOptions {
  userId: string;
  imageUrl: string;
}

export async function uploadProfilePicFacebook({
  userId,
  imageUrl,
}: UploadProfilePicFacebookOptions) {
  await refreshAccessToken();

  const auth = getAuth();
  const refreshToken = auth.getRefreshToken();

  const res = await makeRequest({
    baseUri: config.IAM_API_URI,
    path: `/users/${userId}/profile-image/url`,
    method: 'put',
    throwOnError: true,
    data: {
      ProfileImageUrl: imageUrl,
      RefreshToken: refreshToken,
    },
    withBearerAuthHeader: true,
  });

  return res.body.Result;
}

export function isLoggedIn() {
  try {
    const auth = getAuth();
    const authCookie = auth.getParsedAuthCookie();

    return authCookie ? hasConsumerPermissions(authCookie.accessToken) : false;
  } catch {
    return false;
  }
}

interface ApiResponse {
  status: number;
  body: ApiResponseBody;
}

type Result = {
  access_token: string;
  refresh_token: string;
  '.issued': string;
  '.expires': string;
};

interface ApiResponseBody {
  Errors: { Name: string }[] | null;
  Result: Result | null;
}

function isInvalidCredentialsResponse({ status, body }: ApiResponse) {
  return status === 400 && hasError('Username-Password', body);
}

function isEmailAddressInUseResponse({ status, body }: ApiResponse) {
  return status === 409 && hasError('EmailAddress', body);
}

function hasError(errorName: string, body: ApiResponseBody) {
  if (body.Errors && body.Errors.length > 0) {
    return body.Errors[0].Name === errorName;
  }

  return false;
}

interface JWT {
  iss: string;
  sub: string;
  aud: string;
  exp: string;
  nbf: string;
  src: string;
  ver: string;
  role: string;
  given_name: string;
  family_name: string;
  picture: string;
  permissions: string | string[];
  unique_name: string;
}

function hasConsumerPermissions(acessToken: string) {
  try {
    const jwt = decode<JWT>(acessToken);
    const permissions = formatPermissions(jwt.permissions);

    return (
      permissions.find(permission => permission.groupType === 'consumer') !==
      undefined
    );
  } catch {
    return false;
  }
}

type Permission = {
  groupGuid: string;
  groupType: string;
  groupTypeExternalId: string;
  role: string[];
};

const formatPermissions = (permissions: string | string[]): Permission[] => {
  return typeof permissions === 'string'
    ? [parsePermission(permissions)]
    : permissions.map(parsePermission);
};

const parsePermission = (permission: string): Permission => {
  const [groupGuid, groupType, groupTypeExternalId, role] = permission.split(
    ','
  );

  return {
    groupGuid,
    groupType,
    groupTypeExternalId,
    role: role.split('::'),
  };
};



// WEBPACK FOOTER //
// ./src/helpers/api/iam.ts